import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { FileTypeEnum } from 'api/documents';
import { ContextEnum } from 'api/files';
import { documentsAPI, filesAPI } from 'services/swagger-api';

export type IdentityVerificationStatus =
  | 'idle'
  | 'uploading'
  | 'ocr_running'
  | 'manual'
  | 'success'
  | 'upload_error';

export interface IdentityVerificationState {
  status: IdentityVerificationStatus;
  storedFileId: string | null;
  fileName: string | null;
  previewUrl: string | null;
  previewMimeType: string | null;
  errorMessage: string | null;
  mismatched: boolean;
  isSubmitting: boolean;
}

const INITIAL_STATE: IdentityVerificationState = {
  status: 'idle',
  storedFileId: null,
  fileName: null,
  previewUrl: null,
  previewMimeType: null,
  errorMessage: null,
  mismatched: false,
  isSubmitting: false
};

const EXTENSION_MIME_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp'
};

const guessMimeTypeFromFileName = (fileName: string | undefined): string | undefined => {
  if (!fileName) return undefined;
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension ? EXTENSION_MIME_TYPES[extension] : undefined;
};

const extractFileNameFromUrl = (url: string): string | undefined => {
  try {
    const pathname = new URL(url).pathname;
    return pathname.split('/').pop();
  } catch {
    return undefined;
  }
};

const resolveMimeType = (blobType: string, remoteUrl: string): string => {
  const cleanedBlobType =
    blobType && blobType !== 'application/octet-stream' ? blobType : undefined;
  return (
    cleanedBlobType ||
    guessMimeTypeFromFileName(extractFileNameFromUrl(remoteUrl)) ||
    'application/pdf'
  );
};

export interface UseIdentityVerificationOptions {
  naturalPersonId: string;
  onSuccess: (storedFileId: string) => void;
  initialStoredFileId?: string;
}

export const useIdentityVerification = ({
  naturalPersonId,
  onSuccess,
  initialStoredFileId
}: UseIdentityVerificationOptions) => {
  const { t } = useTranslation(NsKey.IdentityUpload);
  const [state, setState] = useState<IdentityVerificationState>(() =>
    initialStoredFileId
      ? { ...INITIAL_STATE, status: 'manual', storedFileId: initialStoredFileId }
      : INITIAL_STATE
  );
  const previewUrlRef = useRef<string | null>(null);

  const revokePreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  useEffect(() => () => revokePreview(), [revokePreview]);

  const buildPreviewFromStoredFile = useCallback(async (storedFileId: string) => {
    const response = await filesAPI.getTemporaryUrl({ storedFileId });
    if (!response.isSuccess || !response.url) {
      throw new Error(response.errorMessage || 'Preview URL unavailable');
    }
    const fileResponse = await fetch(response.url);
    if (!fileResponse.ok) {
      throw new Error(`Preview fetch failed (HTTP ${fileResponse.status})`);
    }
    const originalBlob = await fileResponse.blob();
    const mimeType = resolveMimeType(originalBlob.type, response.url);
    const blob = new Blob([originalBlob], { type: mimeType });
    return { previewUrl: URL.createObjectURL(blob), mimeType };
  }, []);

  useEffect(() => {
    if (!initialStoredFileId) return;
    let cancelled = false;
    (async () => {
      try {
        const { previewUrl, mimeType } = await buildPreviewFromStoredFile(initialStoredFileId);
        if (cancelled) {
          URL.revokeObjectURL(previewUrl);
          return;
        }
        previewUrlRef.current = previewUrl;
        setState(prev => ({
          ...prev,
          previewUrl,
          previewMimeType: mimeType
        }));
      } catch (error) {
        console.error('Identity preview fetch failed:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialStoredFileId, buildPreviewFromStoredFile]);

  const runOcr = useCallback(
    async (storedFileId: string) => {
      setState(prev => ({ ...prev, status: 'ocr_running', errorMessage: null }));
      try {
        const result = await documentsAPI.extractNamesFromIdentity({
          storedFileId,
          naturalPersonId
        });
        if (result.isSuccess) {
          setState(prev => ({ ...prev, status: 'success' }));
          onSuccess(storedFileId);
          return;
        }
        setState(prev => ({ ...prev, status: 'manual', mismatched: false }));
      } catch (error) {
        console.error('Identity OCR failed:', error);
        setState(prev => ({
          ...prev,
          status: 'manual',
          mismatched: false,
          errorMessage: t('IDENTITY_UPLOAD.ERRORS.OCR_REQUEST_FAILED')
        }));
      }
    },
    [naturalPersonId, onSuccess, t]
  );

  const uploadFile = useCallback(
    async (file: File) => {
      revokePreview();
      setState({
        ...INITIAL_STATE,
        status: 'uploading',
        fileName: file.name
      });
      try {
        const response = await filesAPI.uploadFile({
          fileType: FileTypeEnum.PieceIdentite,
          context: ContextEnum.Person,
          objetId: naturalPersonId,
          file
        });
        const storedFileId = response.id;
        if (!response.isSuccess || !storedFileId) {
          setState({
            ...INITIAL_STATE,
            status: 'upload_error',
            errorMessage: response.errorMessage || t('IDENTITY_UPLOAD.ERRORS.UPLOAD_FAILED')
          });
          return;
        }

        const { previewUrl, mimeType } = await buildPreviewFromStoredFile(storedFileId);
        previewUrlRef.current = previewUrl;

        setState(prev => ({
          ...prev,
          storedFileId,
          fileName: file.name,
          previewUrl,
          previewMimeType: mimeType
        }));

        await runOcr(storedFileId);
      } catch (error) {
        console.error('Identity upload failed:', error);
        setState({
          ...INITIAL_STATE,
          status: 'upload_error',
          errorMessage: t('IDENTITY_UPLOAD.ERRORS.UPLOAD_FAILED')
        });
      }
    },
    [naturalPersonId, revokePreview, runOcr, buildPreviewFromStoredFile, t]
  );

  const submitManual = useCallback(
    async (firstName: string, lastName: string) => {
      if (!state.storedFileId) return;
      setState(prev => ({ ...prev, isSubmitting: true, errorMessage: null }));
      try {
        const result = await documentsAPI.verifyIdentityNames({
          identityVerifyCommand: {
            storedFileId: state.storedFileId,
            naturalPersonId,
            firstName,
            lastName
          }
        });
        if (result.isSuccess) {
          setState(prev => ({ ...prev, status: 'success', isSubmitting: false }));
          onSuccess(state.storedFileId);
          return;
        }
        setState(prev => ({
          ...prev,
          isSubmitting: false,
          mismatched: true,
          errorMessage: result.errorMessage || null
        }));
      } catch (error) {
        console.error('Identity manual verification failed:', error);
        setState(prev => ({
          ...prev,
          isSubmitting: false,
          errorMessage: t('IDENTITY_UPLOAD.ERRORS.VERIFY_REQUEST_FAILED')
        }));
      }
    },
    [naturalPersonId, onSuccess, state.storedFileId, t]
  );

  const retryOcr = useCallback(() => {
    if (!state.storedFileId) return;
    setState(prev => ({ ...prev, errorMessage: null, mismatched: false }));
    runOcr(state.storedFileId);
  }, [runOcr, state.storedFileId]);

  const resetUpload = useCallback(() => {
    revokePreview();
    setState(INITIAL_STATE);
  }, [revokePreview]);

  return {
    state,
    uploadFile,
    submitManual,
    retryOcr,
    resetUpload
  };
};
